import {EventEmitter, Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import {apiEndpoints, CommonConstant, platformIconPath, platformIconPathById, KpiStatus, listOrderedPlatforms} from '../../app.constants';
import {
  ChannelAudienceKPI,
  ContactPermissionStatus,
  GaugeCircleChartData,
  InfluencerChannel,
  InfluencerConfirmationForm,
  InfluencerDetail,
  InfluencerIgStoryLoadingStatus,
  InfluencerPost,
  RequestInfluencerType,
  TopExposure,
  ContentPieChart,
  PieChartValue,
  ProgressBar,
  ChannelFollowers
} from '../../../models/influencer';
import {Channel, Kpi, KpiItem} from '../../../models/common';
import {BackendHttpService} from '../../core/backend-http.service';
import {TranslateService} from '@ngx-translate/core';
import {CommonService} from '../common/common.service';
import {MeasureSearchCriteria} from '../../../models/measure/measure-search-criteria';
import {Router} from '@angular/router';
import {DemographicHypeAuditor} from '../../../models/demographic/demographic-hype-auditor';
import {ObjectUtils} from '../../utils/object.utils';
import {InfluencerStatus, NewInfluencer, NewInfluencerChanel} from '../../../models/influencer/new-influencer';
import {ErrorTemplate} from '../../../models/ErrorTemplate';
import {InfluencerNote} from '../../../models/influencer-note';
import {DiscoverPost} from '../../../models/discover';
import {InfluencerIgNewStory} from '../../../models/influencer-ig-new-story';
import {CacheService} from '../cache/cache.service';
import {UserService} from '../user.service';
import {InfluencerPreloadData} from '../../../models/influencer-preload-data';
import {forkJoin} from 'rxjs/observable/forkJoin';
import {Subscription} from 'rxjs/Subscription';
import {UpdateChannelRequest} from '../../../models/influencer-setting';
import {StringUtils} from '../../utils/string.utils';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import { Subject } from 'rxjs/Subject';
import {DateUtils} from '../../utils/date.utils';
import {PendingInfluencer, PendingInfluencerChannelStatus, PendingInfluencerCountryPriority} from '../../../models/influencer/pending-influencer';
import { FollowerGrowth } from '../../../models/influencer/follower-growth';
import { map } from 'rxjs/operators';
import { PostType } from '../../../models/post-overview';

declare var require: any;

@Injectable()
export class InfluencerService extends BackendHttpService<any> {
  private static readonly DEMOGRAPHIC_EXPIRED_DAYS = 30;
  private influencerData: InfluencerDetail;
  private preloadInfluencerData: InfluencerPreloadData;
  private preloadingSubscriptions: Subscription[] = [];
  private _cachedInfluencerUpdatedSubject = new Subject<any>();
  public loadingNewStoriesEvent: EventEmitter<InfluencerIgStoryLoadingStatus>;
  private influencerIgNewStory: InfluencerIgNewStory;
  private _activeChannelSubject: BehaviorSubject<any> = new BehaviorSubject(null);
  private isConfirmNameAndEmailSubject: Subject<boolean> = new BehaviorSubject<boolean>(false);
  public creditAvailability = new BehaviorSubject<boolean>(null);
  private _activePendingInfluencer: BehaviorSubject<PendingInfluencer> = new BehaviorSubject(null);
  private listPendingInfluencer: PendingInfluencer[] = [];
  private totalPendingInfluencer: number;
  private numberOfPendingInfluencersInCampaign: number;

  private invalidNewInfluencerData: any;
  constructor(protected router: Router,
              protected translate: TranslateService,
              private userService: UserService,
              private cacheService: CacheService,
              private commonService: CommonService) {
    super(router, translate);
    this.loadingNewStoriesEvent = new EventEmitter<InfluencerIgStoryLoadingStatus>();
  }

  get invalidNewInfluencer() {
    return this.invalidNewInfluencerData;
  }

  get activeChannel(): Observable<any> {
    return this._activeChannelSubject.asObservable();
  }

  get currentActiveChannel(): any {
    return this._activeChannelSubject.getValue();
  }

  updateActiveChannel(channel) {
    this._activeChannelSubject.next(channel);
  }

  clearInvalidInfluencerData() {
    this.invalidNewInfluencerData = null;
  }

  get influencerDetails() {
    return this.influencerData;
  }

  setInfluencerDetails(influencer) {
    this.influencerData = influencer;
  }

  clearCachedInfluencerData() {
    this.influencerData = null;
  }

  getInfluencerPosts(influencerId, params: any) {
    return this.get(apiEndpoints.getInfluencerPosts.replace(':influencerId', influencerId), params).map(res => {
      if (!res || !res.data) {
        return [];
      }
      const normalizedPostData = res.data.map(post => Object.assign({}, post, {
        story: post.isStory,
        video: post.isVideo,
        completed: post.isStory && post.isVideo ? post.completed : true
      }));
      return this.transformDiscoverPostDataIntoInfluencerPost(normalizedPostData);
    });
  }

  transformDiscoverPostDataIntoInfluencerPost(dPosts: DiscoverPost[], isFakeStory?: boolean) {
    return dPosts.map((item) => {
      const post = new InfluencerPost();
      post.blog_id = item.influencerId;
      post.influencerName = item.influencerName;
      post.date = item.publishedDate;
      post.imageUrl = isFakeStory ? 'http://static.getachoo.com/example-story-example.jpg' : item.imageUrl;
      post.videoUrl = item.videoUrl;
      post.influencerName = item.influencerName;
      post.mentions = isFakeStory ? [] : item.mentions;
      post.relativepath = item.relativePath;
      post.url = isFakeStory ? 'https://static.getachoo.com/example-story.mp4' : item.fullUrl;
      post.title = isFakeStory ? null : item.title;
      post.urlHash = item.urlHash;
      post.platformId = item.platform ? item.platform.id : null;
      post.platform = item.platform;
      post.reach = item.reach;
      post.impact = item.impact * 100;
      post.engagement = item.engagement;
      post.isStory = item.story;
      post.completed = item.completed;
      post.isVideo = this.isVideoPost(item);
      post.mediaWidth = item.mediaWidth;
      post.mediaHeight = item.mediaHeight;
      post.channelId = item.channelId;
      post.postType = item.postType;
      post.interaction = item.interaction;
      return post;
    });
  }

  /**
   * Checking if a post should be shown as video like Instagram Story/Reel or Tiktok
   * @param {DiscoverPost} post
   * @returns {DiscoverPost | any | boolean}
   */
   public isVideoPost(post: DiscoverPost) {
    return post && post.platform
      && (
        this.isInstagramStoryVideo(post)
        || this.isInstagramReelVideo(post)
        || (post.platform.id === platformIconPath.tiktok.id)
      );
  }

  public isInstagramStoryVideo(post:  DiscoverPost): boolean {
    return post && post.platform && post.platform.id === platformIconPath.instagram.id && post.story && post.video;
  }

  private isInstagramReelVideo(post: DiscoverPost): boolean {
    return post && post.platform && post.platform.id === platformIconPath.instagram.id
      && post.postType === PostType.REEL
      && post.video
      && !StringUtils.isUndefinedNullOrEmpty(post.videoUrl);
  }

  getPlatformIconById(id: number) {
    return platformIconPathById[id] !== undefined ? platformIconPathById[id].path : '';
  }

  getPlatformNameById(id: number) {
    return platformIconPathById[id] !== undefined ? platformIconPathById[id].name : '';
  }

  getInstagramAudienceData(influencerId) {
    const apiUrl = `${apiEndpoints.getInstagramAudienceData}/${influencerId}`;
    return this.get(apiUrl);
  }

  checkViewDemographicPermission(companyId, channelId) {
    // do not need to perform validation for Admin
    if (this.userService.isAdmin) {
      return Observable.of(true);
    }
    return this.get(apiEndpoints.checkDemographic.replace(':companyId', companyId).replace(':channelId', channelId));
  }

  getInfluencer(influencerId, companyId?: number): Observable<InfluencerDetail> {
    let influencer;
    // find influencer from preloading data
    if (this.preloadInfluencerData) {
      influencer = this.preloadInfluencerData.influencerList.find(infl => infl.influencerId === Number(influencerId));
    }
    if (influencer && !influencer.isOutdated) {
      return Observable.of(this.normalizeInfluencerData(influencer));
    } else {
      let apiUrl = `${apiEndpoints.getInfluencerById}/${influencerId}`;
      if (companyId) {
        apiUrl = apiUrl + `?companyId=${companyId}`;
      }
      return this.get(apiUrl)
        .map((res: any) => this.normalizeInfluencerData(res.data))
        .catch((err) => Observable.of(null));
    }
  }

  private normalizeInfluencerData(result): InfluencerDetail {
    if (result) {
      const influencer = new InfluencerDetail();
      influencer.id = result.influencerId;
      influencer.profilePicture = result.profilePicture;
      influencer.name = result.influencerName;
      influencer.domain = result.domain;
      influencer.country = result.country;
      influencer.description = result.description;
      influencer.platforms = result.platforms;
      influencer.numberOfPosts = result.numberOfPosts;
      influencer.platformReaches = result.platformReaches;
      influencer.topTopics = result.topTopics;
      influencer.topMentions = result.topMentions;
      influencer.average = result.averageKpis;
      influencer.channelAverageKpis = result.channelAverageKpis;
      influencer.creditAvailability = result.creditAvailability;
      influencer.rawChannelGlobalKpis = result.channelGlobalKpis;
      influencer.relatedInfluencers = result.relatedInfluencers;
      influencer.brandMentions = result.brandMentions;
      influencer.sponsoredOrganicPercentage = result.sponsoredOrganicPercentage;

      if (result.channels) {
        influencer.channels = result.channels.map((channel) =>
          Object.assign({}, channel, {
            platformName: channel.platform.name
          })
        );
      }

      if (result.channelGlobalKpis && result.channelGlobalKpis.length > 0) {
        influencer.channelGlobalKpis = result.channelGlobalKpis.map(channelKpi => this.normalizeChannelKpi(channelKpi));
      }

      if (result.globalKpi) {
        influencer.globalKpi = this.normalizeChannelKpi(result.globalKpi);
      }

      this.getChannelAudienceKPIs(result.influencerId).subscribe((influencerFollowers) => {
        let followers = 0;
        influencerFollowers.forEach(function (follower) {
          followers += follower.value;
        });
        influencer.numberOfFollowers = followers;
      });

      this.influencerData = influencer;

      return influencer;
    }
  }

  private normalizeChannelKpi(channelKpi): Kpi {
    const formattedKpi = new Kpi();
    formattedKpi.engagement = new KpiItem();
    formattedKpi.engagement.value = channelKpi.engagement;
    formattedKpi.engagement.name = 'engagement';
    formattedKpi.engagement.percent = Math.abs(channelKpi.engagement_change) * 100;
    formattedKpi.engagement.status = (channelKpi.engagement_change >= 0) ? 'asc' : 'desc';

    formattedKpi.impact = new KpiItem();
    formattedKpi.impact.value = channelKpi.impact * 100;
    formattedKpi.impact.name = 'impact';
    formattedKpi.impact.percent = Math.abs(channelKpi.impact_change) * 100;
    formattedKpi.impact.status = (channelKpi.impact_change >= 0) ? 'asc' : 'desc';

    formattedKpi.impressions = new KpiItem();
    formattedKpi.impressions.value = channelKpi.impression;
    formattedKpi.impressions.name = 'impressions';
    formattedKpi.impressions.percent = Math.abs(channelKpi.impression_change) * 100;
    formattedKpi.impressions.status = (channelKpi.impression_change >= 0) ? 'asc' : 'desc';

    formattedKpi.reach = new KpiItem();
    formattedKpi.reach.value = channelKpi.reach;
    formattedKpi.reach.name = 'reach';
    formattedKpi.reach.percent = Math.abs(channelKpi.reach_change) * 100;
    formattedKpi.reach.status = (channelKpi.reach_change >= 0) ? 'asc' : 'desc';

    if (channelKpi.channel_id) {
      formattedKpi.channelId = Number(channelKpi.channel_id);
    } else {
      formattedKpi.channelId = null;
    }
    return formattedKpi;
  }

  private getChannelAudienceKPIs(influencerId): Observable<ChannelAudienceKPI[]> {
    const apiUrl = `${apiEndpoints.getChannelAudienceKPIs}/${influencerId}`;

    return this.get(apiUrl).map((res: any) => {
      return res && res.data ? res.data.channelAudienceKPIs : [];
    });
  }

  /**
   * This method is used to load data of PlatformReach
   * @param {MeasureSearchCriteria} measure
   * @returns {Observable<PlatformReach[]>}
   */
  public getPlatformReach(measure: MeasureSearchCriteria): Observable<GaugeCircleChartData[]> {
    return this.get(apiEndpoints.getExposurePlatforms, measure).map((res: any) =>
      res.platforms.map((platform) => Object.assign({}, platform, {
          description: this.translate.instant('INFLUENCER.insight.platform_reach.impact', {
            impact: Number(platform.progressBar.value).toFixed(1)
          })
        })
      )
    );
  }

  /**
   * this method is used to load TopExposure data of influencer or project
   * @param params
   * @returns {Observable<any>}
   */
  public getExposures(params) {
    return this.get(apiEndpoints.getExposures, params);
  }

  public getTopExposures(influencerId, params) {
    return this.get(apiEndpoints.getTopExposures.replace(':influencerId', influencerId), params)
      .map((res: any) => res && res.data ? this.normalizeTopExposureData(res.data) : null);
  }

  private normalizeTopExposureData(topExposure) {
    const reach = topExposure.reach || {};
    const engagement = topExposure.engagement || {};
    const impact = topExposure.impact || {};
    const data = {
      reach: new TopExposure(reach.influencerName, reach.influencerId, reach.relativePath, reach.fullUrl, reach.urlHash,
        reach.publishedDate, reach.imageUrl, reach.title, reach.reach, reach.platform),
      engagement: new TopExposure(engagement.influencerName, engagement.influencerId, engagement.relativePath, engagement.fullUrl, engagement.urlHash,
        engagement.publishedDate, engagement.imageUrl, engagement.title, engagement.engagement, engagement.platform),
      impact: new TopExposure(impact.influencerName, impact.influencerId, impact.relativePath, impact.fullUrl, impact.urlHash,
        impact.publishedDate, impact.imageUrl, impact.title, impact.impact * 100, impact.platform),
    };
    return data;
  }

  /**
   * this method is used to load BestPerformingExposure data of influencer or project
   * @param params
   * @returns {Observable<any>}
   */
  public getBestPerforming(influencerId, params) {
    return this.get(apiEndpoints.getBestPerforming.replace(':influencerId', influencerId), params)
      .map((res: any) => res && res.data ? this.normalizeBestPerfomingData(res.data) : null);
  }

  private normalizeBestPerfomingData(bestPerfomingPost) {
    return bestPerfomingPost.map((item) => ({
      pieCharts: {
        engagement: { value: item.engagement},
        reach: { value: item.reach},
        impact: { value: item.impact * 100 }
      },
      platform: item.platform ? item.platform.id : 0,
      published_date: item.publishedDate,
      text: item.title,
      image: item.imageUrl,
      influencer_id: item.influencerId,
      url: item.fullUrl,
      urlHash: item.urlHash,
      relativepath: item.relativePath,
      impact: item.impact * 100,
      interaction: item.interaction
    }));
  }

  public getDailyKpiTimeLineChart(influencerId, params) {
    return this.get(apiEndpoints.getDailyKpiTimeLineChart.replace(':influencerId', influencerId), params).map((res: any) => {
      if (res && res.data) {
        const dailyKpis = this.filterDailyKpiByChannel(res.data, params.channelId);
        const data = dailyKpis.sort((a, b) => a.date - b.date); // For ascending sort date
        return this.commonService.normalizeDataForActivityLineChart(data);
      } else {
        return null;
      }
    }).catch((err) => Observable.of(null));
  }

  private filterDailyKpiByChannel(dailyKpis, channelId: number) {
    if (channelId) {
      return dailyKpis.filter(d => d.channel && d.channel.id === channelId);
    } else {
      return dailyKpis.filter(d => !d.channel);
    }
  }

  /**
   * this method use to load KPI data of Influencer
   * @param {number} influencerId
   * @param {number} period
   * @returns {any}
   */
  public getPerformanceGlobalKPIs(params) {
    return this.get(apiEndpoints.getPerformanceGlobalKPIs, params).map((res: any) => {
      const kpiData = new Kpi();
      res.data.KPIs.forEach((item: any) => {
        switch (item.name.toLowerCase()) {
          case CommonConstant.IMPRESSIONS_NAME.toLowerCase():
            kpiData.impressions = item;
            break;
          case CommonConstant.REACH_NAME.toLowerCase():
            kpiData.reach = item;
            break;
          case CommonConstant.IMPACT_NAME.toLowerCase():
            kpiData.impact = item;
            break;
          case CommonConstant.ENGAGEMENT_NAME.toLowerCase():
            kpiData.engagement = item;
            break;
          case CommonConstant.MENTIONS_NAME.toLowerCase():
            kpiData.mention = item;
            break;
          default:
            break;
        }
      });
      return kpiData;
    });
  }

  /**
   * to get Campaign list of an influencer
   * @param params
   * @returns {any}
   */
  public getCampaignListOfInfluencer(params): any {
    return this.get(apiEndpoints.getAllCampaignsForInfluencer, params).map((res: any) => {
      return res.data.map((campaign) => this.normalizeCampaignData(campaign));
    });
  }

  private normalizeCampaignData(campaign) {
    return {
      name: campaign.campaign_name,
      id: campaign.campaign_influencer_id,
      campaignId: campaign.campaign_id,
      campaignInfluencerId: campaign.campaign_influencer_id,
      status: campaign.status,
      campaignType: campaign.campaign_status,
      companyId: campaign.campaign_company_id
    };
  }

  public getCampaignInfluencerData(campaignInfluencerId) {
    let campaignPosts;
    if (this.preloadInfluencerData && this.preloadInfluencerData.campaignPostList) {
      campaignPosts = this.preloadInfluencerData.campaignPostList[campaignInfluencerId];
    }
    if (campaignPosts) {
      return Observable.of(campaignPosts);
    } else {
      return this.get(apiEndpoints.getCampaignInfluencerData.replace(':campaignInfluencerId', campaignInfluencerId)).map(res => res.data);
    }
  }

  /**
   * to get Campaign KPI of an influencer
   * @param params
   * @returns {any}
   */
  public getCampaignInfluencerKPIs(campaignInfluencerId): any {
    return this.get(apiEndpoints.getCampaignInfluencerKPIs + campaignInfluencerId)
      .map((res: any) => {
        const postKpi = new Kpi();
        postKpi.engagement.value = res.data.engagement;
        postKpi.impact.value = res.data.impact;
        postKpi.impressions.value = res.data.impression;
        postKpi.reach.value = res.data.reach;
        postKpi.post.value = res.data.post;
        return postKpi;
      });
  }

  /**
   * to get Campaign details of an influencer
   * @param campaignInfluencerId
   * @returns {any}
   */
  public getCampaignInfluencerDetail(campaignInfluencerId): any {
    let campaignInfluencerDetails;
    if (this.preloadInfluencerData && this.preloadInfluencerData.campaignInfluencerDetails) {
      campaignInfluencerDetails = this.preloadInfluencerData.campaignInfluencerDetails.find(camp => camp.campaignInfluencerId === Number(campaignInfluencerId));
    }
    if (campaignInfluencerDetails) {
      return Observable.of(campaignInfluencerDetails);
    } else {
      return this.get(apiEndpoints.getCampaignInfluencerDetail + campaignInfluencerId)
        .map((res: any) => res && res.data ? res.data : null);
    }
  }

  /**
   * to update campaign status of an influencer
   * @param data
   * @returns {any}
   */
  public updateInfluencerCampaignStatus(data): any {
    return this.post(apiEndpoints.updateInfluencerCampaignStatus, data);
  }

  /**
   * Call API to accept the campaign invitation
   * @param {number} campaignInfluencerId
   * @param {string} accessLevel
   * @returns {Observable<any>}
   */
  public acceptCampaignInvitation(campaignId: number, influencerId: number, accessLevel = 'influencer') {
    return this.post(apiEndpoints.acceptCampaignInvitation, {
      campaignId,
      influencerId,
      accessLevel
    });
  }

  /**
   * Call API to decline the campaign invitation
   * @param {number} campaignInfluencerId
   * @param {string} accessLevel
   * @returns {Observable<any>}
   */
  public declineCampaignInvitation(campaignId: number, influencerId: number, accessLevel = 'influencer') {
    return this.post(apiEndpoints.declineCampaignInvitation, {
      campaignId,
      influencerId,
      accessLevel
    });
  }

  private executeInfluencEyeRequest(influencerChannelId: number, companyId: number): Observable<any> {
    return this.get(apiEndpoints.loadDemographic.replace(':channelId', influencerChannelId.toString()), { companyId });
  }

  public loadInfluencEyeData(influencerChannelId: number, companyId: number): Observable<DemographicHypeAuditor> {
    return this.executeInfluencEyeRequest(influencerChannelId, companyId).map(res => res && res.data ? this.mapDemographicData(res.data) : null);
  }

  public updateInfluencerDemographic(channelId, companyId) {
    return this.get(apiEndpoints.updateDemographic.replace(':channelId', channelId.toString()), { companyId })
      .map((res) => res && res.data ? this.mapDemographicData(res.data) : null);
  }

  private mapDemographicData(rawDemographic) {
    const demographic = new DemographicHypeAuditor();
    demographic.username = rawDemographic.handle;
    demographic.genders = ObjectUtils.parseJSON(rawDemographic.demography);
    demographic.countries = ObjectUtils.parseJSON(rawDemographic.followersGeography).countries;
    demographic.audienceInterest = ObjectUtils.parseJSON(rawDemographic.audienceThematics);
    demographic.audienceType = ObjectUtils.parseJSON(rawDemographic.likersReach);
    demographic.authenticEngagement = ObjectUtils.parseJSON(rawDemographic.followersReach);
    demographic.cities = ObjectUtils.parseJSON(rawDemographic.followersGeography).cities;
    demographic.languages = ObjectUtils.parseJSON(rawDemographic.followersLanguages);
    demographic.demographyByAge = ObjectUtils.parseJSON(rawDemographic.demographyByAge);
    const daysDiff = DateUtils.computeTimeDiffInHour(new Date(rawDemographic.modifiedAt), new Date()) / 24;
    demographic.isNew = this.isDemographicNew(rawDemographic);
    demographic.isOutdated = daysDiff > InfluencerService.DEMOGRAPHIC_EXPIRED_DAYS
      || (rawDemographic.version && rawDemographic.version < 2);
    return demographic;
  }

  /*
  Detect where demographic is new created from BE
  */
  private isDemographicNew(rawDemographic): boolean {
    let dateNumber = rawDemographic.modifiedAt;
    dateNumber = (dateNumber === null || dateNumber === undefined)
      ? rawDemographic.createdAt
      : dateNumber;

    if (dateNumber === null || dateNumber === undefined) {
      return false;
    }

    const minutesDiff = DateUtils.computeTimeDiffInHour(new Date(dateNumber), new Date()) * 60;
    return minutesDiff < 1;
  }

  public addInfluencerByHandle(newInfluencer: NewInfluencer, influencerChannels: NewInfluencerChanel[]): Observable<any> {
    const data = {
      channels: influencerChannels
    };

    return this.post(apiEndpoints.addInfluencerByHandle, data);
  }

  public validateInfluencerData(newInfluencer: NewInfluencer, influencerChannels: NewInfluencerChanel[]) {
    const errors: ErrorTemplate[] = [];
    const validPlatforms = [
      platformIconPath.instagram.id,
      platformIconPath.blogs.id,
      platformIconPath.youtube.id,
      platformIconPath.tiktok.id
    ];

    const hasMandatoryChannel = influencerChannels.find((ic) => validPlatforms.indexOf(ic.platform_id) > -1);

    if (!hasMandatoryChannel) {
      errors.push({
        name: 'newInfluencer.channels',
        message: this.translate.instant('add_influencer_manually.validation.missing_maandatory_channel')
      });
    }

    influencerChannels.forEach((channel) => {
      const channelPlatform = platformIconPathById[channel.platform_id];
      if (channel && (!channel.handle || channel.handle === '')) {
        errors.push({
          name: channelPlatform.name.toLowerCase() + '.handle',
          message: this.translate.instant('add_influencer_manually.validation.missing_handle', {
            platform: channelPlatform.name
          })
        });
      }
    });
    return errors;
  }

  public validateInfluencerConfirmationData(influencer: InfluencerConfirmationForm, acceptedTermAndService: boolean) {
    const errors: ErrorTemplate[] = [];
    if (!influencer.fullName || StringUtils.trim(influencer.fullName) === '') {
      errors.push({
        name: 'influencer.fullName',
        message: this.translate.instant('INFLUENCER.confirmation.missing_full_name')
      });
    }

    if (!influencer.email || StringUtils.trim(influencer.email) === '') {
      errors.push({
        name: 'influencer.email',
        message: this.translate.instant('INFLUENCER.confirmation.missing_email')
      });
    } else if (influencer.email.indexOf('@') <= 0) {
      errors.push({
        name: 'influencer.email',
        message: this.translate.instant('INFLUENCER.confirmation.wrong_email')
      });
    }
    if (!acceptedTermAndService) {
      errors.push({
        name: 'influencer.acceptedTermAndService',
        message: this.translate.instant('INFLUENCER.confirmation.accept_term_service')
      });
    }
    return errors;
  }


    /**
   * add new influencer manually
   * @param {NewInfluencer} newInfluencer
   * @param {NewInfluencerChanel[]} influencerChannels
   * @param afterAddingCallback
   */
  public addNewInfluencer(newInfluencer, influencerChannels: NewInfluencerChanel[], afterAddingCallback, errorCallback) {
    // if influencer already existed. Don't create influencer again. Just add channel
    if (newInfluencer.id > 0) {
      this.addChannelToInfluencer(newInfluencer, influencerChannels, afterAddingCallback, errorCallback);
      return;
    }

    // get influencer name. If it's undefined. Use it's IG handle
    let influencerName = newInfluencer.domain;
    if (!influencerName && influencerChannels.length > 0) {
      influencerName = influencerChannels[0].handle;
    }
    const data = {
      country: newInfluencer.country,
      domain: influencerName,
      email: newInfluencer.email,
      gender: newInfluencer.gender,
      isActive: InfluencerStatus.NEW,
      languages: newInfluencer.languages,
      name: influencerName,
      network: '',
      pending_completed: null,
      system_email: newInfluencer.systemEmail
    };

    this.post(apiEndpoints.addInfluencer, data).subscribe((res) => {
      this.addChannelToInfluencer(res.data, influencerChannels, afterAddingCallback, errorCallback);
      return res;
    }, (err) => {
      if (errorCallback) {
        errorCallback(err);
        this.invalidNewInfluencerData = { newInfluencer, influencerChannels };
      }
    });
  }

  public addChannelToInfluencer(influencer, influencerChannels: NewInfluencerChanel[], afterAddingCallback, errorCallback) {
    // stop adding channel if empty
    if (influencerChannels.length === 0) {
      afterAddingCallback(influencer);
      return;
    }
    const influencerChannelObservers = [];
    influencerChannels.forEach((channel) => {
      if (channel.handle && channel.handle !== '') {
        influencerChannelObservers.push(this.addInfluencerChannel(influencer.id, channel));
      }
    });
    Observable.forkJoin(influencerChannelObservers).subscribe((r2) => {
      // run callback
      if (afterAddingCallback) {
        afterAddingCallback(influencer);
      }
    }, (err) => {
      if (errorCallback) {
        errorCallback(err);
        this.invalidNewInfluencerData = { newInfluencer: influencer, influencerChannels };
      }
    });
  }

  public deleteInfluencerChannel(channelId) {
    return this.post(apiEndpoints.deleteChannel, {
      id: channelId
    });
  }

  public createOrUpdateNote(note: InfluencerNote) {
    return this.post(apiEndpoints.influencerNote.replace(':influencerId', note.influencerId.toString()), note);
  }

  public loadNoteListOfInfluencer(influencerId: number) {
    return this.get(apiEndpoints.influencerNote.replace(':influencerId', influencerId.toString()));
  }

  public deleteNote(noteId: number) {
    return this.delete(apiEndpoints.deleteNote.replace(':noteId', noteId.toString()));
  }

  public addInfluencerChannel(influencerId: number, handle: NewInfluencerChanel) {
    const data = Object.assign({}, handle, {
      blog_id: influencerId
    });
    return this.post(apiEndpoints.addChannel, data);
  }

  public addChannelByInfluencer(influencerId: number, handle: NewInfluencerChanel) {
    const data = Object.assign({}, handle, {
      blog_id: influencerId
    });
    return this.post(apiEndpoints.addChannelByUser, data);
  }

  public updateInfluencerChannel(channelId: number, channelData: UpdateChannelRequest) {
    return this.post(apiEndpoints.updateChannel, channelData).map(res => res ? res.data : null);
  }

  public precheckInfluencerChannel(channelId: number, channelData: UpdateChannelRequest) {
    return this.post(apiEndpoints.precheckChannel, channelData).map(res => res ? res.data : null);
  }

  public getInfluencerLoginStatus(channelId) {
    return this.get(apiEndpoints.loginStatus, { channelId }).map(res => res ? res.data : null);
  }

  public getInfluencerChannels(influencerId): Observable<Channel[]> {
    return this.get(apiEndpoints.getInfluencerChannels.replace(':influencerId', influencerId)).map(res => res ? res.data : null);
  }

  /**
   * using this method to fire event when getting new status from server
   * @param {InfluencerIgStoryLoadingStatus} newStatus
   */
  public updateNewIgStory(igNewStoryLoader: InfluencerIgNewStory) {
    this.influencerIgNewStory = igNewStoryLoader;
    this.loadingNewStoriesEvent.emit(igNewStoryLoader.status);
  }

  public updateNewIgStoryStatus(newStatus: InfluencerIgStoryLoadingStatus) {
    if (!this.influencerIgNewStory) {
      this.influencerIgNewStory = new InfluencerIgNewStory();
    }
    this.influencerIgNewStory.status = newStatus;
    this.loadingNewStoriesEvent.emit(newStatus);
  }

  public getLatestNewStory() {
    return this.influencerIgNewStory;
  }

  public clearNewIgStory() {
    this.influencerIgNewStory = null;
    this.loadingNewStoriesEvent.emit(null);
  }

  public fetchNewIgStories(influencerId: number, lastSeenTime, loaderIds?: string) {
    let storyRequest: Observable<any>;
    let params = {};
    if (loaderIds) {
      params = Object.assign({}, params, {
        loaderIds
      });
    }
    if (lastSeenTime) {
      params = Object.assign({}, params, {
        lastSeenTime
      });
    }
    storyRequest = this.get(apiEndpoints.influencerNewStories.replace(':influencerId', String(influencerId)), params);
    return storyRequest.map((res) => {
      if (res && res.data) {
        this.updateNewIgStory(res.data);
        return res.data;
      } else {
        return null;
      }
    });
  }

  public getNewIgStories() {
    return this.influencerIgNewStory ? this.influencerIgNewStory.newIgStories : [];
  }

  /**
   * influencer decline the campaign invitation
   * @param campaignInfluencerId
   * @returns {Observable<any>}
   */
  public rejectCampaign(campaignInfluencerId) {
    return this.post(apiEndpoints.rejectCampaign, {
      campaignInfluencerId,
      accessLevel: 'influencer'
    });
  }

  /**
   * Preloading data of influencer belonged to a group
   * @param {number[]} influencerIds
   * @param {number[]} campaignInfluencerIds
   * @param {boolean} loadMore
   * @param {number} limit
   * @param {number} page
   * @param {number} pageSize
   */
  public preloadInfluencerDataByIdList(influencerIds: number[], campaignInfluencerIds: number[], loadMore: boolean,
                                       limit = 20, page = CommonConstant.START_PAGE_INDEX, pageSize = 50) {
    if (!loadMore) {
      this.cancelCurrentPreloadingRequest();
    }
    const chunkedInfluencerList = this.chunkInfluencerList(influencerIds);

    chunkedInfluencerList.forEach(chunk => {
      this.preloadInfluencerListInternally(chunk, campaignInfluencerIds, limit, page, pageSize);
    });
  }

  /**
   * preloading data of influencer belonged to campagin list
   * @param {any[]} influencer
   * @param {boolean} loadMore
   * @param {number} limit
   * @param {number} page
   * @param {number} pageSize
   */
  public preloadCampaignInfluencerDataByIdList(influencer: any[], loadMore: boolean,
                                       limit = 20, page = CommonConstant.START_PAGE_INDEX, pageSize = 50) {
    if (!loadMore) {
      this.cancelCurrentPreloadingRequest();
    }
    const chunkedInfluencerList = this.chunkInfluencerList(influencer);

    chunkedInfluencerList.forEach(chunk => {
      const campaignInfluencerIds = chunk.map(inf => inf.campaign_influencer_id);
      const influencerList = chunk.map(inf => inf.influencer_id);
      this.preloadInfluencerListInternally(influencerList, campaignInfluencerIds, limit, page, pageSize);
    });
  }

  public loadListChannelOfInfluencer(influencerId: number): Observable<InfluencerChannel[]> {
    return this.get(apiEndpoints.getInfluencerChannels.replace(':influencerId', influencerId.toString()))
      .map((res) => res && res.data ? res.data : []);
  }

  private preloadInfluencerListInternally(influencerIds: number[], campaignInfluencerIds: number[], limit, page, pageSize) {
    // start fetching new preloading data
    const data = {
      influencers: influencerIds,
      period: CommonConstant.PERIOD_30_DAYS,
      limit
    };
    const requests = [
      this.post(apiEndpoints.loadInfluencerList, data).map((res) => res ? res.data : []),
      this.post(`${apiEndpoints.getCampaignsOfInfluencerList}?company_id=${this.userService.getCompanyId()}`, {
        influencers: influencerIds,
        page,
        pageSize
      }).map(res => res ? res.data : []),
    ];
    if (Array.isArray(campaignInfluencerIds) && campaignInfluencerIds.length > 0) {
      const campaignRequest = {
        influencers: influencerIds,
        campaignInfluencerIds: campaignInfluencerIds,
        page,
        pageSize
      };
      requests.push(this.post(apiEndpoints.getCampaignInfluencerDetailList, campaignRequest).map(res => res ? res.data : []));
    }
    const preloadingSubscription = forkJoin(...requests).subscribe((res) => {
      if (!this.preloadInfluencerData) {
        this.preloadInfluencerData = new InfluencerPreloadData();
      }
      this.preloadInfluencerData.influencerList.push(...res[0]);
      this.preloadInfluencerData.campaignInfluencerList.push(...res[1]);
      if (res.length <= 2) {
        this.preloadCampaignInfluencerData(res[1], page, pageSize);
      } else {
        this.preloadInfluencerData.campaignInfluencerDetails.push(...res[2]);
      }
    });
    this.preloadingSubscriptions.push(preloadingSubscription);
  }

  private preloadCampaignInfluencerData(campaignInfluencerList, page, pageSize) {
    // don't preload campaign data if influencer doesn't join any campaign yet
    if (!campaignInfluencerList || campaignInfluencerList.length === 0) {
      return;
    }
    const campaignInfluencerIds = campaignInfluencerList.map(ci => ci.campaign_influencer_id);
    const campaignRequest = {
      campaignInfluencerIds: campaignInfluencerIds,
      page,
      pageSize
    };
    const preloadCampSubscription = this.post(apiEndpoints.getCampaignInfluencerDetailList, campaignRequest)
      .map(res => res ? res.data : [])
      .subscribe((res) => {
        if (!this.preloadInfluencerData) {
          this.preloadInfluencerData = new InfluencerPreloadData();
        }
        this.preloadInfluencerData.campaignInfluencerDetails.push(...res);
      }
    );
    this.preloadingSubscriptions.push(preloadCampSubscription);
  }

  public extractHADemographicReportGeneratingTime(message) {
    const numb = message.match(/\d+/g);
    return numb.length > 0 ? numb[0] : null;
  }

  public cancelCurrentPreloadingRequest() {
    this.preloadInfluencerData = null;
    this.preloadingSubscriptions.forEach(sub => {
      if (sub) {
        sub.unsubscribe();
      }
    });
  }

  public updateInfluencerData(influencerId: number, influencerData): Observable<any> {
    return this.post(apiEndpoints.updateInfluencer.replace(':influencerId', influencerId.toString()), influencerData);
  }


  /**
   * Send a request permission for contact from advertiser to influencer
   * @param {number} companyId, {number} influencerId
   * @returns {Observable<any>}
   */
  requestPermission(companyId, influencerId) {
    return this.post(apiEndpoints.requestPermission.replace(':companyId', companyId)
      .replace(':influencerId', influencerId), {}).map(res => res ? res.data : null);
  }

  /**
   * Call to accept contact permission from advertiser
   * @param {string} token
   * @returns {Observable<any>}
   */
  public acceptContactPermission(token: string) {
    return this.post(apiEndpoints.contactPermission, {
      status: ContactPermissionStatus.ACCEPTED,
      token
    }).map(res => res ? res.data : null);
  }

  /**
   * Call to reset contact permission from advertiser
   * @param {string} token
   * @returns {Observable<any>}
   */
  public resetContactPermission(token: string) {
    return this.post(apiEndpoints.contactPermission, {
      status: ContactPermissionStatus.REQUESTED,
      token
    }).map(res => res ? res.data : null);
  }

  /**
   * Call to reject contact permission from advertiser
   * @param {string} token
   * @returns {Observable<any>}
   */
  public rejectContactPermission(token: string) {
    return this.post(apiEndpoints.contactPermission, {
      status: ContactPermissionStatus.REJECTED,
      token
    }).map(res => res ? res.data : null);
  }

  private chunkInfluencerList(influencerList) {
    const chunkedList = [];
    let tempArray = [];
    let i, j;
    const chunkSize = 50;
    for (i = 0, j = influencerList.length; i < j; i += chunkSize) {
      tempArray = influencerList.slice(i, i + chunkSize);
      chunkedList.push(tempArray);
    }
    return chunkedList;
  }

  public confirmInfluencer(influencerId: number, influencer: InfluencerConfirmationForm) {
    return this.post(apiEndpoints.confirmInfluencer.replace(':influencerId', influencerId.toString()), influencer);
  }

  /**
   * Request send a new Campaign Invitation
   * @param {number} influencerId
   * @param {number} campaignId
   * @param {RequestInfluencerType} requestType
   * @returns {Observable<any>}
   */
  public requestNewCampaignInvitation(influencerId: number, campaignInfluencerId: number, requestType: RequestInfluencerType) {
    return this.post(apiEndpoints.requestNewInvitation.replace(':influencerId', influencerId.toString()), {
      requestType: requestType,
      id: campaignInfluencerId
    });
  }

  /**
   * Request send a new Contact Permission request
   * @param {number} influencerId
   * @param {number} contactPermissionId
   * @param {RequestInfluencerType} requestType
   * @returns {Observable<any>}
   */
  public requestNewContactPermissionInvitation(influencerId: number, contactPermissionId: number, requestType: RequestInfluencerType) {
    return this.post(apiEndpoints.requestNewInvitation.replace(':influencerId', influencerId.toString()), {
      requestType: requestType,
      id: contactPermissionId
    });
  }
  /**
   * fetch list channel belonged to given influencer
   * @param influencerId
   * @returns {Observable<any>}
   */
  fetchListChannel(influencerId) {
    return this.cacheService.get(`influencer_channels_${influencerId}`, this.loadListChannelOfInfluencer(influencerId))
      .map((res) => res.map(c => ({
        id: c.id,
        handle: c.handle,
        name: c.platform ? `@${c.handle}` : null,
        platformId: c.platform ? c.platform.id : null,
        platformName: c.platform ? c.platform.name.toLowerCase() : '',
        isAuthorized: c.type && c.type === 1
      })));
  }

  public activeInfluencer(influencerId: number) {
    return this.post(apiEndpoints.activeInfluencer.replace(':influencerId', influencerId.toString()), {});
  }

  public visitInfluencer(influencerId: number, companyId: number) {
    // not allow to mark influencer as visted for Admin, Influencer and Analyst account.
    if (this.userService.isAdmin || this.userService.isInfluencer || this.userService.isAnalyst) {
      return Observable.of(null);
    }
    const url = apiEndpoints.visitInfluencer.replace(':influencerId', influencerId.toString());
    const fullUrl = `${url}?companyId=${companyId}`;
    return this.post(fullUrl, {});
  }

  get confirmInfluencerNameAndEmailObserver(): Observable<boolean> {
    return this.isConfirmNameAndEmailSubject.asObservable();
  }

  confirmingInfluencerNameAndEmail() {
    this.isConfirmNameAndEmailSubject.next(true);
  }

  confirmedInfluencerNameAndEmail() {
    this.isConfirmNameAndEmailSubject.next(false);
  }

  checkInfluencerCreditAvailability(influencerId, companyId: number) {
    const apiUrl = `${apiEndpoints.getInfluencerById}/${influencerId}?companyId=${companyId}`;
    return this.get(apiUrl).subscribe((res: any) => {
        if (res && res.data) {
          this.creditAvailability.next(res.data.creditAvailability);
          this.updateCachingInfluencerData(influencerId, res.data);
        }
    });
  }

  private updateCachingInfluencerData(influencerId, updatedData) {
    // update preloading data
    let influencerIndex;
    if (this.preloadInfluencerData) {
      influencerIndex = this.preloadInfluencerData.influencerList.findIndex(infl => infl.influencerId === Number(influencerId));
    }
    if (influencerIndex >= 0) {
      const preloadedInfluencer = this.preloadInfluencerData.influencerList[influencerIndex];
      preloadedInfluencer.averageKpis = updatedData.averageKpis;
      preloadedInfluencer.channelAverageKpis = updatedData.channelAverageKpis;
      preloadedInfluencer.brandMentions = updatedData.brandMentions;
      preloadedInfluencer.relatedInfluencers = updatedData.relatedInfluencers;
      preloadedInfluencer.sponsoredOrganicPercentage = updatedData.sponsoredOrganicPercentage;
      preloadedInfluencer.channels = updatedData.channels;
      preloadedInfluencer.isOutdated = false;
      this._cachedInfluencerUpdatedSubject.next(updatedData);
      // remove caching data
      this.cacheService.clear(`influencer_${influencerId}`);
    }
  }

  public markPreloadedInfluencerOutdated(influencerId) {
    // mark preloaded influncer outdated
    let influencerIndex;
    if (this.preloadInfluencerData) {
      influencerIndex = this.preloadInfluencerData.influencerList.findIndex(infl => infl.influencerId === Number(influencerId));
    }
    if (influencerIndex >= 0) {
      const preloadedInfluencer = this.preloadInfluencerData.influencerList[influencerIndex];
      preloadedInfluencer.isOutdated = true;
    }

    // clear cache
    const cacheKey = `influencer_${influencerId}`;
    if (this.cacheService.has(cacheKey)) {
      this.cacheService.clear(cacheKey);
    }
  }

  isCreditAvailable() {
    return this.creditAvailability.getValue();
  }

  get creditAvailabilityObserve(): Observable<boolean> {
    return this.creditAvailability.asObservable();
  }

  public get cachedInfluencerUpdated(): Observable<any> {
    return this._cachedInfluencerUpdatedSubject.asObservable();
  }

  normalizeChannelAverageKpi(channelAverageKpis: any[], averageKpis, activeChannel: any): Kpi {
    if (!Array.isArray(channelAverageKpis) || channelAverageKpis.length === 0) {
      return null;
    }
    let average;
    if (activeChannel) {
      average = channelAverageKpis.find(ak => Number(ak.channel_id) === activeChannel.id);
    } else {
      average = averageKpis;
    }
    if (!average || (!average.reach_avg && !average.impression_avg && !average.engagement_avg)) {
      return null;
    }
    const averageKpi = new Kpi();
    averageKpi.isAverage = true;
    averageKpi.reach.value = average.reach_avg || 0;
    averageKpi.reach.name = this.translate.instant('INFLUENCER.insight.kpi.reach');
    averageKpi.impressions.value = average.impression_avg || 0;
    averageKpi.impressions.name = this.translate.instant('INFLUENCER.insight.kpi.impressions');
    averageKpi.engagement.value = average.engagement_avg || 0;
    averageKpi.engagement.name = this.translate.instant('INFLUENCER.insight.kpi.engagement');
    averageKpi.impact.value = average.impact_avg ? average.impact_avg * 100 : 0;
    // engagement rate
    averageKpi.engagementRate.name = this.translate.instant('INFLUENCER.insight.kpi.engagement_rate');
    averageKpi.engagementRate.value = average.engagement_rate_avg ? average.engagement_rate_avg * 100 : 0;
    averageKpi.engagementRate.status = average.engagement_rate_change && average.engagement_rate_change < 0 ? KpiStatus.DESC : KpiStatus.ASC;
    averageKpi.engagementRate.percent = typeof average.engagement_rate_change === 'number' ? Math.abs(Math.round(average.engagement_rate_change * 100)) : null;
    averageKpi.channelId = Number(average.channel_id);
    if (activeChannel) {
      averageKpi.platformName = activeChannel.platformName;
    }
    return averageKpi;
  }

  calculatePlatformReach(platformReachesArr) {
    const activePlatformReaches = [];
    const selectedPlatformReaches = [];
    const platformReachs = [];
    platformReachesArr.forEach(plr => {
      let item = new GaugeCircleChartData();
      const description = this.translate.instant('INFLUENCER.insight.platform_reach.impact', {
        impact: Number(plr.impact * 100).toFixed(1)
      });
      const name =  this.getPlatformNameById(plr.platform);
      item = this.calculateGaugeCircleChartData(plr.reachChange, plr.reach, name, plr.reachChange, plr.reachPercent, null, description, 'reach');

      platformReachs.push(item);
      const platform = listOrderedPlatforms.find(p => p.id === plr.platform);
      activePlatformReaches.push(platform);
      selectedPlatformReaches.push(plr.platform);
    });
    return {
      platformReachs: platformReachs,
      activePlatformReaches: activePlatformReaches,
      selectedPlatformReaches: selectedPlatformReaches
    };
  }

  getChannelFollowerData(influencerId, channelIds) {
    let apiUrl = apiEndpoints.channelFollowers.replace(':influencerId', influencerId);
    let channelIdsString = null;
    if (channelIds) {
      channelIdsString = channelIds.toString();
    }
    apiUrl = channelIdsString ? apiUrl + `?channelIds=${channelIdsString}` : apiUrl;
    return this.get(apiUrl).map(res => res && res.data ? res.data : null);
  }

  calculateChannelFollower(channelFollowersData: ChannelFollowers) {
    const calculatedChannelFollower = [];
    const selectedChannelFollower = [];
    if (channelFollowersData.channels.length > 0) {
      channelFollowersData.channels = channelFollowersData.channels.sort((a, b) => b.followerCount - a.followerCount).slice(0, 4);
      channelFollowersData.channels.forEach(cl => {
        let item = new GaugeCircleChartData();
        const progressValue = cl.followerCount / channelFollowersData.influencer.followerCount;
        item = this.calculateGaugeCircleChartData(cl.followerCountChange, cl.followerCount, cl.handle, cl.followerCountChange, progressValue, cl.platformId);
        item.contentPieChart.reach.tooltipInfo = cl.lastUpdated ? this.translate.instant('INFLUENCER.last_updated_follower', {
          last_updated: DateUtils.convertDateToString(cl.lastUpdated, 'dd MMM yyyy')
        }) : null;
        calculatedChannelFollower.push(item);
        selectedChannelFollower.push(cl.channelId);
      });
    }
    return {
      calculatedChannelFollower: calculatedChannelFollower,
      selectedChannelFollower: selectedChannelFollower
    };
  }

  private calculateGaugeCircleChartData(percent, value, name, valueChange, progressValue, platformId?, description?, contentPieChartName?) {
    const item = new GaugeCircleChartData();
    item.contentPieChart = new ContentPieChart();
    item.contentPieChart.reach = new PieChartValue();
    item.contentPieChart.reach.percent = Math.abs(percent) * 100;
    item.contentPieChart.reach.value = value;
    item.contentPieChart.reach.status = (valueChange >= 0) ? KpiStatus.ASC : KpiStatus.DESC;

    item.progressBar = new ProgressBar();
    item.progressBar.value = progressValue * 100;

    item.description = description;
    item.name = name;
    if (contentPieChartName) {
      item.contentPieChart.reach.name = contentPieChartName;
      item.progressBar.name = contentPieChartName;
    }
    if (platformId > 0) {
      item.platformName = listOrderedPlatforms.filter(lp => lp.id === platformId)[0].name.toLowerCase();
    }
    return item;
  }

  get activePendingInfluencer(): Observable<PendingInfluencer> {
    return this._activePendingInfluencer.asObservable();
  }

  public loadPendingInfluencerList(complete, error) {
    this.get(apiEndpoints.getPendingInfluencers, {
      count: 5
    }).subscribe((res) => {
      if (res && res.data) {
        this.handlePendingInfluencerList(res.data);
        complete();
      }
    }, (err) => {
      error(err);
    });
  }

  private handlePendingInfluencerList(data) {
    this.totalPendingInfluencer = data.number_pending_influencer;
    this.numberOfPendingInfluencersInCampaign = data.number_pending_influencer_in_campaign;
    const pendingInfluencers = data.pending_influencers.map(pi => Object.assign({}, pi, {
      languages: pi.languages && pi.languages.length > 0 ? pi.languages[0] : null,
      ...this.getPendingInfluencerCountryPriorityInfo(pi)
    }));
    if (pendingInfluencers.length > 0) {
      this.listPendingInfluencer.push(...pendingInfluencers);
      this._activePendingInfluencer.next(pendingInfluencers[0]);
    }
  }

  private getPendingInfluencerCountryPriorityInfo(pi): any {
    let countryPriority: PendingInfluencerCountryPriority = null;
    let countryPriorityUserName: string = null;

    if (pi.country_priority) {
      const priority = pi.country_priority;

      switch (priority.priority) {
        case 1:
          {
            if (priority.user) {
              const userRoleId = priority.user.role_id;

              switch (userRoleId) {
                case 1:
                  countryPriority = PendingInfluencerCountryPriority.SET_BY_ADMIN;
                  break;

                case 2:
                  countryPriority = PendingInfluencerCountryPriority.SET_BY_USER;
                  break;

                case 3:
                  countryPriority = PendingInfluencerCountryPriority.SET_BY_ANALYST;
                  break;

                default:
                  countryPriority = PendingInfluencerCountryPriority.SET_MANUALLY;
                  break;
              }

              countryPriorityUserName = priority.user.user_full_name;
            } else {
              countryPriority = PendingInfluencerCountryPriority.SET_MANUALLY;
            }
          }
          break;

        case 2:
          countryPriority = PendingInfluencerCountryPriority.DISCOVERED_VIA_FLAG;
          break;

        case 3:
          countryPriority = PendingInfluencerCountryPriority.DISCOVERED_VIA_POST_GEO_TAGS;
          break;

        case 4:
          countryPriority = PendingInfluencerCountryPriority.DISCOVERED_VIA_POST_PHONE_NO;
          break;

        default:
          countryPriority = null;
          break;
      }
    }

    return {countryPriority, countryPriorityUserName};
  }

  public getNextPendingInfluencer(complete, error) {
    const currentPendingInfluencer = this._activePendingInfluencer.getValue();
    if (!currentPendingInfluencer) {
      complete();
      return;
    }
    const currentPendingInfluencerIdx = this.listPendingInfluencer.findIndex(pi => pi.id === currentPendingInfluencer.id);
    if (currentPendingInfluencerIdx < 0) {
      error();
    } else if (currentPendingInfluencerIdx < this.listPendingInfluencer.length - 1) {
      this._activePendingInfluencer.next(this.listPendingInfluencer[currentPendingInfluencerIdx + 1]);
      complete();
    } else {
      this.loadPendingInfluencerList(complete, error);
    }
  }

  public updatePendingInfluencerError(processingInfluencer: PendingInfluencer) {
    this._activePendingInfluencer.next(processingInfluencer);
  }

  public getPreviousPendingInfluencer(complete, error) {
    const currentPendingInfluencer = this._activePendingInfluencer.getValue();
    if (!currentPendingInfluencer) {
      complete();
      return;
    }
    const currentPendingInfluencerIdx = this.listPendingInfluencer.findIndex(pi => pi.id === currentPendingInfluencer.id);
    if (currentPendingInfluencerIdx <= 0) {
      error();
    } else {
      const previousInfluencer = this.listPendingInfluencer[currentPendingInfluencerIdx - 1];
      this.get(apiEndpoints.getPendingInfluencerById.replace(':influencer_id', previousInfluencer.id.toString())).subscribe((res) => {
        if (res && res.data) {
          const pendingInfluencer = res.data.pending_influencer;
          const normalizedPendingInfluencer = Object.assign({}, pendingInfluencer, {
            languages: pendingInfluencer.languages && pendingInfluencer.languages.length > 0 ? pendingInfluencer.languages[0] : null
          });
          this._activePendingInfluencer.next(normalizedPendingInfluencer);
          this.updatePendingInfluencerList(normalizedPendingInfluencer);
        }
        complete();
      }, (err) => {
        error();
      });
    }
  }

  public isLastPendingInfluencer(pendingInfluencer: PendingInfluencer) {
    return this.listPendingInfluencer.length > 0 && this.listPendingInfluencer[this.listPendingInfluencer.length - 1].id === pendingInfluencer.id;
  }

  public updatePendingInfluencer(pendingInfluencer: PendingInfluencer, newChannelIds: number[], updatedChannelIds: number[]) {
    const data = Object.assign({}, pendingInfluencer, {
      new_channels: newChannelIds,
      updated_channels: updatedChannelIds,
      languages: [pendingInfluencer.languages]
    });
    delete data.channels;

    return this.put(apiEndpoints.getPendingInfluencerById.replace(':influencer_id', pendingInfluencer.id.toString()), data).map((res) => {
      this.totalPendingInfluencer = res.data.number_pending_influencer;
      this.numberOfPendingInfluencersInCampaign = res.data.number_pending_influencer_in_campaign;
      return res;
    });
  }

  public getTotalPendingInfluencer() {
    return this.totalPendingInfluencer;
  }

  public getNumberOfPendingInfluencersInCampaign() {
    return this.numberOfPendingInfluencersInCampaign;
  }

  public updatePendingInfluencerList(pendingInfluencer: PendingInfluencer) {
    const updatedIndex = this.listPendingInfluencer.findIndex(pi => pi.id === pendingInfluencer.id);
    this.listPendingInfluencer.splice(updatedIndex, 1, pendingInfluencer);
  }

  /**
   * get influencer folllower growth
   * @param {number} influencerId
   * @param {number} channelId
   */
  public getFollowerGrowth(channelId: number): Observable<FollowerGrowth[]> {
    const url = apiEndpoints.getFollowerGrowth.replace(':channelId', channelId.toString());
    return this.get(url).pipe(map(res => {
      return res && res.data && res.data.followerGrowth;
    }));
  }

  /**
   * get influencer folllowers
   * @param {number} influencerId
   */
   public getInfluencerFollowers(influencerId: number): Observable<any> {
    const url = apiEndpoints.getInfluencerFollowers.replace(':influencerId', influencerId.toString());
    return this.get(url).pipe(map(res => {
      return res && res.data;
    }));
  }

  /**
   * get Channel Interaction Rate Development
   * @param {number} channelId
   */
   public getChannelInteractionRateDevelopment(channelId: number): Observable<any> {
    const url = apiEndpoints.getChannelInteractionRateDevelopment.replace(':channelId', channelId.toString());
    return this.get(url).pipe(map(res => {
      return res && res.data && res.data.engagementRateGrowth;
    }));
  }
}



// WEBPACK FOOTER //
// ./src/app/services/influencer/influencer.service.ts